/**
 * TransmettreSonBateau — Landing page dédiée aux familles / propriétaires
 * confrontés à la transmission (succession, donation, cession familiale) d'un
 * bateau. Formulaire court avec CTA "Demander mon bilan confidentiel".
 */
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Anchor, Heart, ClipboardList, Sparkles, ShieldCheck, Clock, MapPin,
  CheckCircle2, Loader2, ArrowRight, Mail, Phone, User as UserIcon,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import useCanonical from '../hooks/useCanonical';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SITUATIONS = [
  { icon: Heart, title: 'Succession et héritage', text: 'Vous venez d\'hériter d\'un bateau et vous ne savez pas quoi en faire, ni ce qu\'il vaut vraiment.' },
  { icon: Anchor, title: 'Bateau qui ne sert plus', text: 'Un parent âgé ne navigue plus. Le bateau prend l\'eau, les frais s\'accumulent, la famille est loin.' },
  { icon: ClipboardList, title: 'Donation ou cession familiale', text: 'Vous voulez transmettre à un enfant, un neveu, un ami — mais sans conflit ni erreur d\'évaluation.' },
  { icon: MapPin, title: 'Bateau éloigné géographiquement', text: 'Il est amarré loin de chez vous, personne dans la famille ne peut s\'en occuper au quotidien.' },
];

const PAIN_POINTS = [
  'Aucune idée de la valeur réelle du bateau — peur de le brader ou de le surestimer.',
  'Frais de port, d\'entretien et d\'assurance qui continuent à courir chaque mois.',
  'Documents perdus ou incomplets (carte de circulation, francisation, factures d\'entretien).',
  'Bateau qui se dégrade faute d\'usage — osmose, moteur, gréement…',
  'Tensions familiales sur "qu\'est-ce qu\'on en fait ?" et sur le partage éventuel.',
  'Démarches administratives complexes (mutation, radiation, cession, TVA).',
  'Peur de tomber sur un acheteur peu scrupuleux ou un intermédiaire opaque.',
];

const STEPS = [
  { n: '01', title: 'Bilan confidentiel gratuit', text: 'Échange téléphonique de 20 min. J\'écoute votre situation, je réponds à vos questions, sans engagement.' },
  { n: '02', title: 'État de la valeur', text: 'À partir des photos, documents et de mes bases marché, je vous donne une fourchette de prix réaliste et argumentée.' },
  { n: '03', title: 'Recommandation claire', text: 'Vendre maintenant ? Attendre ? Restaurer ? Céder à un membre de la famille ? Je vous dis ce qui est raisonnable dans votre cas.' },
  { n: '04', title: 'Accompagnement à la carte', text: 'Si vous décidez de vendre, je peux prendre en charge la mise en vente, la sélection des acheteurs et la négociation.' },
];

const BILAN_INCLUDES = [
  'Analyse rapide des documents transmis (carte de circulation, factures, photos)',
  'Estimation de la valeur marché en état actuel',
  'Identification des points de vigilance techniques et administratifs',
  'Trois scénarios possibles (vendre en l\'état, restaurer, garder) avec leurs implications',
  'Recommandation personnalisée écrite envoyée par email',
  'Sans engagement — c\'est offert et confidentiel',
];

const REASSURE = [
  { icon: ShieldCheck, title: 'Indépendant et confidentiel', text: 'Je ne représente aucune marque, aucun stock. Vos informations restent strictement entre nous.' },
  { icon: Clock, title: '30 ans dans la vente et la négociation', text: 'J\'ai passé plus de 30 ans à défendre des acheteurs et vendeurs. La transmission d\'un bien est un moment délicat, je le sais.' },
  { icon: MapPin, title: 'Basé à La Rochelle, disponible partout en France', text: 'Le bateau peut être n\'importe où — je me déplace ou j\'organise l\'expertise à distance selon les cas.' },
];

const initialForm = {
  nom: '', email: '', telephone: '', localisation: '', situation: '', message: '',
  website: '', // honeypot
};

export default function TransmettreSonBateau() {
  useCanonical('https://validatedboat.fr/transmettre-son-bateau');

  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.email.trim()) {
      setError('Nom et email obligatoires.');
      return;
    }
    setStatus('loading');
    setError(null);
    try {
      const message = [
        form.situation && `Situation : ${form.situation}`,
        form.message && `Message : ${form.message}`,
        form.localisation && `Bateau localisé : ${form.localisation}`,
      ].filter(Boolean).join('\n');
      const res = await fetch(`${API_URL}/api/quote-request/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service: 'transmettre-bateau',
          service_label: 'Bilan transmission de bateau',
          nom: form.nom,
          email: form.email,
          telephone: form.telephone,
          zone: form.localisation,
          message,
          website: form.website,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Transmettre son bateau — Bilan confidentiel gratuit | ValidatedBoat</title>
        <meta
          name="description"
          content="Vous héritez d'un bateau, votre famille souhaite le transmettre ou le céder ? Bilan confidentiel gratuit, estimation de valeur et accompagnement personnalisé par Nicolas Daval, expert nautique indépendant à La Rochelle."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:title" content="Transmettre son bateau — Bilan confidentiel gratuit | ValidatedBoat" />
        <meta property="og:description" content="Accompagnement humain et indépendant pour la transmission d'un bateau familial. Bilan gratuit et confidentiel." />
        <meta property="og:url" content="https://validatedboat.fr/transmettre-son-bateau" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-[#1a1c1b] text-[#dfddd6]">
        <Header />

        {/* HERO */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.pexels.com/photos/1295036/pexels-photo-1295036.jpeg?auto=compress&cs=tinysrgb&w=1920"
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#1a1c1b]/85 via-[#1a1c1b]/70 to-[#1a1c1b]"></div>
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-10 text-center">
            <p className="text-[#d9fb06] font-bold uppercase text-xs tracking-widest mb-4">
              Transmission · Succession · Cession familiale
            </p>
            <h1 className="font-black text-4xl md:text-6xl leading-tight text-[#dfddd6] uppercase mb-6" data-testid="transmettre-hero-title">
              Transmettre ou <span className="text-[#d9fb06]">céder un bateau</span> sans se tromper
            </h1>
            <p className="text-lg md:text-xl text-[#dfddd6]/80 mb-10 max-w-2xl mx-auto">
              Vous héritez d'un bateau, un parent souhaite le transmettre, ou vous voulez le céder à un proche ?
              Je vous aide à faire les bons choix, sereinement et sans engagement.
            </p>
            <a href="#bilan" data-testid="hero-cta">
              <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-8 py-6 text-base font-bold uppercase">
                Demander mon bilan confidentiel <ArrowRight className="ml-2" size={18} />
              </Button>
            </a>
            <p className="text-[#888680] text-xs mt-4">Gratuit · Confidentiel · Sans engagement</p>
          </div>
        </section>

        {/* SITUATIONS */}
        <section className="py-20 md:py-28 bg-[#1a1c1b]">
          <div className="max-w-6xl mx-auto px-5 md:px-10">
            <div className="text-left mb-12">
              <p className="text-[#d9fb06] font-bold uppercase text-xs tracking-widest mb-3">Situations concernées</p>
              <h2 className="font-black text-3xl md:text-5xl leading-tight text-[#dfddd6] uppercase max-w-3xl">
                Vous êtes peut-être dans <span className="text-[#d9fb06]">un de ces cas</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {SITUATIONS.map((s) => (
                <div key={s.title} className="bg-[#302f2c] border border-[#3f4816] rounded-xl p-6 flex gap-4 hover:border-[#d9fb06]/60 transition-colors">
                  <div className="flex-shrink-0 bg-[#d9fb06]/10 border border-[#d9fb06]/30 rounded-full w-12 h-12 flex items-center justify-center">
                    <s.icon className="text-[#d9fb06]" size={22} />
                  </div>
                  <div>
                    <h3 className="text-[#d9fb06] font-bold uppercase text-sm mb-2">{s.title}</h3>
                    <p className="text-[#dfddd6]/85 text-sm leading-relaxed">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PAIN POINTS */}
        <section className="py-20 md:py-28 bg-[#302f2c]">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <div className="text-left mb-10">
              <p className="text-[#d9fb06] font-bold uppercase text-xs tracking-widest mb-3">Ce que vivent les familles</p>
              <h2 className="font-black text-3xl md:text-5xl leading-tight text-[#dfddd6] uppercase">
                Les <span className="text-[#d9fb06]">problèmes</span> les plus fréquents
              </h2>
            </div>
            <ul className="space-y-3">
              {PAIN_POINTS.map((p, i) => (
                <li key={i} className="flex items-start gap-3 bg-[#1a1c1b] border border-[#3f4816] rounded-lg p-4">
                  <span className="text-[#d9fb06] font-black flex-shrink-0 mt-0.5">•</span>
                  <span className="text-[#dfddd6]/90 text-sm md:text-base leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* STEPS */}
        <section className="py-20 md:py-28 bg-[#1a1c1b]">
          <div className="max-w-6xl mx-auto px-5 md:px-10">
            <div className="text-left mb-12">
              <p className="text-[#d9fb06] font-bold uppercase text-xs tracking-widest mb-3">Mon accompagnement</p>
              <h2 className="font-black text-3xl md:text-5xl leading-tight text-[#dfddd6] uppercase max-w-3xl">
                Quatre étapes <span className="text-[#d9fb06]">simples et humaines</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {STEPS.map((s) => (
                <div key={s.n} className="bg-[#302f2c] border border-[#3f4816] rounded-xl p-6 relative overflow-hidden group hover:border-[#d9fb06]/60 transition-colors">
                  <span className="absolute -top-2 -right-2 text-[6rem] font-black text-[#d9fb06]/8 group-hover:text-[#d9fb06]/15 transition-colors leading-none">{s.n}</span>
                  <div className="relative z-10">
                    <p className="text-[#d9fb06] font-black text-xs mb-3">Étape {s.n}</p>
                    <h3 className="text-[#dfddd6] font-bold uppercase text-base mb-3">{s.title}</h3>
                    <p className="text-[#888680] text-sm leading-relaxed">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BILAN GRATUIT */}
        <section className="py-20 md:py-28 bg-[#302f2c]">
          <div className="max-w-5xl mx-auto px-5 md:px-10">
            <div className="grid md:grid-cols-2 gap-10 items-start">
              <div>
                <p className="text-[#d9fb06] font-bold uppercase text-xs tracking-widest mb-3">Bilan gratuit</p>
                <h2 className="font-black text-3xl md:text-5xl leading-tight text-[#dfddd6] uppercase mb-5">
                  Ce que <span className="text-[#d9fb06]">comprend le bilan</span> gratuit
                </h2>
                <p className="text-[#dfddd6]/80 leading-relaxed mb-5">
                  Un premier échange concret, sans engagement, pour vous donner une visibilité claire sur la valeur du bateau et les options qui s'offrent à vous.
                </p>
                <div className="bg-gradient-to-r from-[#d9fb06]/10 to-transparent border-l-4 border-[#d9fb06] rounded-r-lg p-4">
                  <p className="text-[#d9fb06] font-bold text-sm mb-1">Durée : environ 20 min</p>
                  <p className="text-[#dfddd6]/85 text-sm">Réponse écrite personnalisée sous 48 h ouvrées.</p>
                </div>
              </div>
              <ul className="space-y-3">
                {BILAN_INCLUDES.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 bg-[#1a1c1b] border border-[#3f4816] rounded-lg p-3">
                    <CheckCircle2 className="text-[#d9fb06] flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-[#dfddd6] text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* REASSURANCE */}
        <section className="py-20 md:py-28 bg-[#1a1c1b]">
          <div className="max-w-6xl mx-auto px-5 md:px-10">
            <div className="text-left mb-12">
              <p className="text-[#d9fb06] font-bold uppercase text-xs tracking-widest mb-3">Mon approche</p>
              <h2 className="font-black text-3xl md:text-5xl leading-tight text-[#dfddd6] uppercase max-w-3xl">
                Pourquoi <span className="text-[#d9fb06]">me faire confiance</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {REASSURE.map((r) => (
                <div key={r.title} className="bg-[#302f2c] border border-[#3f4816] rounded-xl p-6">
                  <r.icon className="text-[#d9fb06] mb-3" size={26} />
                  <h3 className="text-[#dfddd6] font-bold uppercase text-sm mb-2">{r.title}</h3>
                  <p className="text-[#888680] text-sm leading-relaxed">{r.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FORM */}
        <section id="bilan" className="py-20 md:py-28 bg-[#302f2c]">
          <div className="max-w-2xl mx-auto px-5 md:px-10">
            <div className="text-center mb-10">
              <Sparkles className="mx-auto text-[#d9fb06] mb-4" size={32} />
              <h2 className="font-black text-3xl md:text-4xl leading-tight text-[#d9fb06] uppercase mb-4">
                Demander mon bilan confidentiel
              </h2>
              <p className="text-[#dfddd6]/80">
                Trois champs suffisent pour démarrer. Je vous rappelle sous 48 h ouvrées.
              </p>
            </div>

            {status === 'success' ? (
              <div className="bg-[#1a1c1b] border-2 border-[#d9fb06] rounded-2xl p-8 text-center" data-testid="transmettre-success">
                <CheckCircle2 className="mx-auto text-[#d9fb06] mb-4" size={56} />
                <h3 className="text-2xl font-black text-[#d9fb06] uppercase mb-3">Demande bien reçue</h3>
                <p className="text-[#dfddd6] mb-2">
                  Merci de votre confiance. Je reviens vers vous très rapidement pour convenir d'un moment d'échange.
                </p>
                <p className="text-[#888680] text-sm">
                  Vos informations restent strictement confidentielles.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-[#1a1c1b] border border-[#3f4816] rounded-2xl p-6 md:p-8 space-y-4" data-testid="transmettre-form">
                {/* honeypot */}
                <input type="text" value={form.website} onChange={set('website')}
                  className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="flex items-center gap-2 text-xs uppercase text-[#d9fb06] font-bold mb-1.5">
                      <UserIcon size={14} /> Nom *
                    </span>
                    <input required value={form.nom} onChange={set('nom')} maxLength={120}
                      className="w-full bg-[#302f2c] border border-[#3f4816] rounded-lg px-4 py-3 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      data-testid="transmettre-input-nom" />
                  </label>
                  <label className="block">
                    <span className="flex items-center gap-2 text-xs uppercase text-[#d9fb06] font-bold mb-1.5">
                      <Mail size={14} /> Email *
                    </span>
                    <input required type="email" value={form.email} onChange={set('email')} maxLength={200}
                      className="w-full bg-[#302f2c] border border-[#3f4816] rounded-lg px-4 py-3 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      data-testid="transmettre-input-email" />
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="flex items-center gap-2 text-xs uppercase text-[#d9fb06] font-bold mb-1.5">
                      <Phone size={14} /> Téléphone
                    </span>
                    <input type="tel" value={form.telephone} onChange={set('telephone')} maxLength={40}
                      placeholder="+33 6 12 34 56 78"
                      className="w-full bg-[#302f2c] border border-[#3f4816] rounded-lg px-4 py-3 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      data-testid="transmettre-input-tel" />
                  </label>
                  <label className="block">
                    <span className="flex items-center gap-2 text-xs uppercase text-[#d9fb06] font-bold mb-1.5">
                      <MapPin size={14} /> Où est le bateau ?
                    </span>
                    <input type="text" value={form.localisation} onChange={set('localisation')} maxLength={200}
                      placeholder="Ville, port ou région"
                      className="w-full bg-[#302f2c] border border-[#3f4816] rounded-lg px-4 py-3 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      data-testid="transmettre-input-loc" />
                  </label>
                </div>

                <label className="block">
                  <span className="text-xs uppercase text-[#d9fb06] font-bold mb-1.5 block">Votre situation</span>
                  <select value={form.situation} onChange={set('situation')}
                    className="w-full bg-[#302f2c] border border-[#3f4816] rounded-lg px-4 py-3 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                    data-testid="transmettre-input-situation">
                    <option value="">— Choisir —</option>
                    <option value="succession">Succession / héritage</option>
                    <option value="bateau-inutilise">Bateau qui ne sert plus</option>
                    <option value="donation">Donation ou cession familiale</option>
                    <option value="eloigne">Bateau trop éloigné</option>
                    <option value="autre">Autre / je préfère en parler</option>
                  </select>
                </label>

                <label className="block">
                  <span className="text-xs uppercase text-[#d9fb06] font-bold mb-1.5 block">Un message ? (optionnel)</span>
                  <textarea rows={3} value={form.message} onChange={set('message')} maxLength={2000}
                    placeholder="Contexte, contraintes, questions particulières…"
                    className="w-full bg-[#302f2c] border border-[#3f4816] rounded-lg px-4 py-3 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none resize-y"
                    data-testid="transmettre-input-message" />
                </label>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm" data-testid="transmettre-error">
                    {error}
                  </div>
                )}

                <Button type="submit" disabled={status === 'loading'}
                  data-testid="transmettre-submit-btn"
                  className="w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full py-6 text-base font-bold uppercase disabled:opacity-50">
                  {status === 'loading' ? (
                    <><Loader2 className="mr-2 animate-spin" size={18} /> Envoi en cours…</>
                  ) : (
                    <>Demander mon bilan confidentiel <ArrowRight className="ml-2" size={18} /></>
                  )}
                </Button>

                <p className="text-[#888680] text-xs text-center leading-relaxed">
                  Vos données restent strictement confidentielles et sont utilisées uniquement pour vous répondre.
                  Aucun partage à des tiers, jamais.
                </p>
              </form>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
